import type { TOptions } from 'i18next';
// eslint-disable-next-line no-restricted-imports
import { Trans, useTranslation } from 'react-i18next';
import { isValidElement, type ReactElement, useCallback } from 'react';
import { useInlineTranslationDialog } from '@/i18n/InlineTranslationProvider';
import { useKeyStore } from '@/i18n/hooks/useKeyStore';

export type TranslationNode =
  | string
  | ReactElement<{
      k: string;
      options?: TOptions;
    }>;

export type TranslationFunction = (key: string, options?: TOptions) => string;

export const useInlineTranslation = (namespace?: string) => {
  const { i18n, ready, t: tOriginal } = useTranslation(namespace);
  const { editMode } = useInlineTranslationDialog();
  const { addKey } = useKeyStore();


  const t = useCallback(
    (key: string, options?: TOptions) => {
      const ns = options?.ns?.toString() || i18n.options.defaultNS?.toString();
      addKey(key, ns, !!options?.count);
      return tOriginal(key, options) + (editMode ? `\{\{${key}\}\}` : '');
    },
    [addKey, editMode, i18n.options.defaultNS, tOriginal],
  );

  const text = (node?: TranslationNode) => {
    if (!node) {
      return '';
    }

    if (typeof node === 'string') {
      return node;
    }

    if (isValidElement<{ k: string; options?: TOptions }>(node)) {
      return t(node.props.k, node.props.options);
    }

    console.error(
      'Invalid node passed to useInlineTranslation -> text():',
      node,
    );
    return '';
  };

  const TransInline = useCallback(
    (props: React.ComponentProps<typeof Trans>) => {
      addKey(
        String(props.i18nKey),
        props?.ns?.toString() || i18n.options.defaultNS?.toString(),
        props?.count !== undefined,
      );
      return <Trans {...props} ns={namespace} />;
    },
    [addKey, i18n.options.defaultNS, namespace],
  );

  return {
    text,
    t,
    i18n,
    Trans: TransInline,
    ready,
  };
};
